/**
 * URL redaction shared by everything that ships a URL to Sentry. Kept free of
 * imports so the API layer can use it without pulling in the Sentry SDK.
 *
 * `beforeSend` only sanitises `event.request.url`; an error *message* goes to
 * Sentry verbatim, so anything building one from a request URL has to redact it
 * here first (UK GDPR: no personal data or credentials in crash reporting).
 */

/** Replaces every MongoDB ObjectId in a URL with `:id`. */
export function sanitizeUrlIds(url: string): string {
  // Global: a path can carry more than one (e.g. /homes/<id>/residents/<id>),
  // and leaving the later ones intact would ship them to Sentry.
  return url.replace(/[0-9a-fA-F]{24}/g, ':id')
}

/**
 * Origin and pathname only. A URL can carry tokens or identifiers in its query
 * string or fragment, and this is shipped to a third party, so those are
 * dropped outright rather than redacted — knowing *which* resource failed is
 * the whole signal, and its parameters add nothing. The remainder still goes
 * through `sanitizeUrlIds` so an ObjectId in the path becomes `:id`.
 */
export function safeResourceUrl(raw: string): string {
  try {
    const base =
      typeof window === 'undefined' ? undefined : window.location.href
    const parsed = new URL(raw, base)

    // A data:/blob: URL's "pathname" is the entire payload — report the scheme
    // and nothing else. blob: keeps a real origin, so it needs naming here
    // rather than falling to the opaque-origin check below.
    if (parsed.protocol === 'data:' || parsed.protocol === 'blob:') {
      return `${parsed.protocol}<stripped>`
    }

    // An opaque origin serialises to the string "null", and for these schemes
    // the pathname is the payload, not a resource path: mailto: holds an email
    // address, file: a local path, javascript: the source itself. Concatenating
    // origin and pathname would ship all of it.
    if (parsed.origin === 'null') {
      return `${parsed.protocol}<stripped>`
    }

    return sanitizeUrlIds(`${parsed.origin}${parsed.pathname}`)
  } catch {
    // Unparseable, so we cannot tell what it holds. Say nothing about it.
    return '<unparseable>'
  }
}
